import { Column, ProgressRing, Row, Text } from "@/components";
import { Skeleton } from "@/components/ui/skeleton";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetWeeklySubmits } from "@/hooks/queries/useGetWeeklySubmits";
import { teamGoalRingColorByField } from "@/lib/constants";
import { TeamGoalField, UserRole } from "@/types";

// Persistent sidebar card: the current user's submits this week vs their
// weekly submit goal. Only shown to staffers (others have no goal).
export const SidebarWeeklySubmits = ({ userId }: { userId: number }) => {
  const { users } = useTaggableUsers();

  const currentUser = users.find((user) => user.id === userId);
  const isStaffer =
    currentUser !== undefined &&
    currentUser.deletedAt === null &&
    currentUser.userRole === UserRole.STAFFER;

  const { data: weeklySubmits, isPending } = useGetWeeklySubmits({
    enabled: isStaffer,
  });

  const render = () => {
    // Non-staffers have no submit goal — the sidebar renders the pet in place.
    if (!isStaffer) return null;

    // Skeleton matches the loaded card's footprint so it doesn't shift when data lands.
    if (isPending) {
      return (
        <Column className="w-full gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <Skeleton className="h-3 w-16" />
          <Row className="items-center gap-3">
            <Skeleton className="h-[72px] w-[72px] shrink-0 rounded-full" />
            <Column className="gap-2">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-4 w-16" />
            </Column>
          </Row>
        </Column>
      );
    }

    if (!weeklySubmits) {
      return (
        <Text className="text-center text-xs text-slate-500">
          Goals unavailable right now.
        </Text>
      );
    }

    return (
      <Column className="w-full gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <Text className="text-xs font-medium uppercase leading-none tracking-wide text-slate-500">
          This week
        </Text>
        <Row>
          <ProgressRing
            label="Submits"
            value={weeklySubmits.submits}
            goal={weeklySubmits.submitsGoal}
            colorClass={teamGoalRingColorByField[TeamGoalField.SUBMITS]}
            layout="row"
            sizePx={72}
          />
        </Row>
      </Column>
    );
  };

  return render();
};
